import { lazy, Suspense, useEffect } from 'react';

import { matchRoute, onLinkClick, usePath } from './app/router.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { useT } from './i18n/index.js';
import { HomePage } from './pages/HomePage.js';
import { RewardsPage } from './pages/RewardsPage.js';
import { loadAuth } from './state/auth.js';
import { RewardToasts } from './rewards/RewardsUi.js';
import { useResolvedTheme, useUiLang } from './state/prefs.js';
import { rewardsStore } from './state/rewards.js';
import { useStore } from './state/store.js';
import { BLOCK_THEMES } from '@pebblecode/shared';

// The editor pulls in Blockly and CodeMirror; the home page should not wait for them.
const ProjectPage = lazy(() =>
  import('./pages/ProjectPage.js').then((m) => ({ default: m.ProjectPage })),
);
const ExerciseOpenPage = lazy(() =>
  import('./pages/OpenPages.js').then((m) => ({ default: m.ExerciseOpenPage })),
);
const SessionPage = lazy(() =>
  import('./pages/SessionPage.js').then((m) => ({ default: m.SessionPage })),
);
const NewProjectPage = lazy(() =>
  import('./pages/OpenPages.js').then((m) => ({ default: m.NewProjectPage })),
);
const auth = () => import('./pages/AuthPages.js');
const LoginPage = lazy(() => auth().then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => auth().then((m) => ({ default: m.SignupPage })));
const ForgotPage = lazy(() => auth().then((m) => ({ default: m.ForgotPage })));
const ResetPage = lazy(() => auth().then((m) => ({ default: m.ResetPage })));
const VerifyPage = lazy(() => auth().then((m) => ({ default: m.VerifyPage })));
const parent = () => import('./pages/ParentPages.js');
const ConsentPage = lazy(() => parent().then((m) => ({ default: m.ConsentPage })));
const ParentManagePage = lazy(() => parent().then((m) => ({ default: m.ParentManagePage })));
const PrivacyPage = lazy(() => parent().then((m) => ({ default: m.PrivacyPage })));
const AccountPage = lazy(() =>
  import('./pages/AccountPage.js').then((m) => ({ default: m.AccountPage })),
);
const AdminPage = lazy(() =>
  import('./pages/AdminPage.js').then((m) => ({ default: m.AdminPage })),
);
const classPages = () => import('./pages/ClassesPages.js');
const ClassesPage = lazy(() => classPages().then((m) => ({ default: m.ClassesPage })));
const ClassPage = lazy(() => classPages().then((m) => ({ default: m.ClassPage })));
const JoinPage = lazy(() => classPages().then((m) => ({ default: m.JoinPage })));
const homeworkPages = () => import('./pages/HomeworkPages.js');
const HomeworkOpenPage = lazy(() => homeworkPages().then((m) => ({ default: m.HomeworkOpenPage })));
const AssignmentPage = lazy(() => homeworkPages().then((m) => ({ default: m.AssignmentPage })));
const ReviewPage = lazy(() => homeworkPages().then((m) => ({ default: m.ReviewPage })));
const exercisePages = () => import('./pages/MyExercisesPages.js');
const MyExercisesPage = lazy(() => exercisePages().then((m) => ({ default: m.MyExercisesPage })));
const ExerciseEditorPage = lazy(() =>
  exercisePages().then((m) => ({ default: m.ExerciseEditorPage })),
);

function useDocumentPrefs() {
  const theme = useResolvedTheme();
  const lang = useUiLang();
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  // An unlocked block colour theme recolours the category squares and miniatures too.
  const blockTheme = useStore(rewardsStore)?.equipped.blockTheme ?? 'classic';
  useEffect(() => {
    const colors = BLOCK_THEMES.find((th) => th.id === blockTheme)?.colors;
    const names = ['logic', 'loops', 'math', 'variables', 'text', 'io', 'lists'];
    const root = document.documentElement.style;
    names.forEach((n, i) => {
      if (blockTheme === 'classic' || !colors) root.removeProperty(`--cat-${n}`);
      else root.setProperty(`--cat-${n}`, colors[i]!);
    });
  }, [blockTheme]);
}

function Loading() {
  const t = useT();
  return <main className="center-page">{t.common.loading}</main>;
}

export function App() {
  useDocumentPrefs();
  useEffect(() => {
    void loadAuth();
  }, []);
  const t = useT();
  const route = matchRoute(usePath());

  const page = (() => {
    switch (route.name) {
      case 'home':
        return <HomePage />;
      case 'project':
        return <ProjectPage id={route.id} />;
      case 'exercise':
        return <ExerciseOpenPage slug={route.slug} />;
      case 'new':
        return <NewProjectPage />;
      case 'session':
        return <SessionPage code={route.code} />;
      case 'rewards':
        return <RewardsPage />;
      case 'login':
        return <LoginPage />;
      case 'signup':
        return <SignupPage />;
      case 'forgot':
        return <ForgotPage />;
      case 'reset':
        return <ResetPage token={route.token} />;
      case 'verify':
        return <VerifyPage token={route.token} />;
      case 'consent':
        return <ConsentPage token={route.token} />;
      case 'parent':
        return <ParentManagePage token={route.token} />;
      case 'privacy':
        return <PrivacyPage />;
      case 'account':
        return <AccountPage />;
      case 'admin':
        return <AdminPage />;
      case 'classes':
        return <ClassesPage />;
      case 'class':
        return <ClassPage id={route.id} />;
      case 'join':
        return <JoinPage code={route.code} />;
      case 'homework':
        return <HomeworkOpenPage id={route.id} />;
      case 'assignment':
        return <AssignmentPage id={route.id} />;
      case 'review':
        return <ReviewPage assignmentId={route.assignmentId} userId={route.userId} />;
      case 'myExercises':
        return <MyExercisesPage />;
      case 'myExercise':
        return <ExerciseEditorPage key={route.slug ?? 'new'} slug={route.slug} />;
      case 'notFound':
        return (
          <main className="center-page">
            <h1>{t.common.notFound}</h1>
            <a href="/" onClick={onLinkClick}>
              {t.common.goHome}
            </a>
          </main>
        );
    }
  })();

  return (
    <ErrorBoundary message={t.common.crashed} reloadLabel={t.common.reload}>
      <Suspense fallback={<Loading />}>{page}</Suspense>
      <RewardToasts />
    </ErrorBoundary>
  );
}
